import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ActivityIndicator, Image, StyleSheet, Text, View } from 'react-native';
import Svg, { Path } from 'react-native-svg';

import { DuelChipStackPng } from '../../../assets/duels';
import { ChipStackPng } from '../../../assets/tournaments/redesign';
import { MyTable, MyTableStatus, RecentTable } from '../../../store/types';
import { timeAgo } from '../../../utils/helpers/date';
import { localizeTableName } from '../../../utils/helpers/localizeTableName';
import { RootStackParamList } from '../../navigator';
import { ACHIEVEMENTS_FONT as FONT } from '../Achievements/achievementsTheme';
import { GradientFill } from '../common/GradientFill';
import { RedesignScrollView } from '../common/RedesignScrollView';
import { Pressable } from '../common/Touchable';
import {
	formatTableChips,
	MY_TABLES_GOLD_FILL,
	myTablesColors,
	tableImage,
	useMyTables,
	useOpenTable,
} from '../MyTables/myTablesShared';
import RedesignModal from './RedesignModal';
import { useRedesignScale } from './useRedesignScale';

/**
 * "My Tables": the popup the My Active & Recent Tables bar opens, after the ticket's mock - a dark
 * maroon panel on a thin gold rim, the gold title over its subtitle with a round close button, a
 * segmented Active / Recent control whose selected half is outlined in gold, and one plate per
 * table. A table is in exactly one tab: the backend keeps seated tables out of the recent list.
 * Every number is in the 390-wide design space and is scaled once.
 */
const PANEL = { height: 470, radius: 14, paddingX: 14, paddingY: 16, gap: 14 };
const PANEL_FILL: Array<[string, string]> = [
	['0', '#2E0806'],
	['1', '#1C0403'],
];
const CLOSE = 30;
const TABS = { height: 34, radius: 8 };
const ROW = { avatar: 52, radius: 10 };
const BUTTON = { width: 86, height: 36, radius: 8 };
const TAB_ACTIVE_FILL: Array<[string, string]> = [
	['0', '#4A1A0A'],
	['1', '#2A0B05'],
];

const STATUS_DOT: Record<MyTableStatus, string> = {
	your_turn: myTablesColors.green,
	in_progress: '#8C7F7A',
	waiting: '#FFC500',
};

type Tab = 'active' | 'recent';
type Nav = NativeStackNavigationProp<RootStackParamList>;

interface MyTablesModalProps {
	onClose: () => void;
}

const MyTablesModal = ({ onClose }: MyTablesModalProps) => {
	const { t } = useTranslation();
	const { navigate } = useNavigation<Nav>();
	const scale = useRedesignScale({ fitHeight: PANEL.height });
	const styles = useMemo(() => createStyles(scale), [scale]);
	const { active, recent, loading } = useMyTables();
	const openTable = useOpenTable();

	const [tab, setTab] = useState<Tab>('active');
	const pickedTab = useRef(false);
	// Open on Recent when there is nothing active but something to rejoin, unless the player has
	// already chosen a tab.
	useEffect(() => {
		if (pickedTab.current || loading) return;
		setTab(active.length === 0 && recent.length > 0 ? 'recent' : 'active');
	}, [active.length, recent.length, loading]);

	const selectTab = (next: Tab) => {
		pickedTab.current = true;
		setTab(next);
	};

	const handleOpen = (table: MyTable | RecentTable) => {
		onClose();
		openTable(table);
	};

	const handleGoToLobby = () => {
		onClose();
		navigate('Lobby');
	};

	const tabs: Array<{ key: Tab; label: string }> = [
		{
			key: 'active',
			label: t('T_SITE_MY_TABLES_TAB_ACTIVE', {
				defaultValue: 'Active tables ({{count}})',
				count: active.length,
			}),
		},
		{
			key: 'recent',
			label: t('T_SITE_MY_TABLES_TAB_RECENT', {
				defaultValue: 'Recent tables ({{count}})',
				count: recent.length,
			}),
		},
	];

	const statusLabel = (status: MyTableStatus) =>
		({
			your_turn: t('T_SITE_MY_TABLES_STATUS_YOUR_TURN', { defaultValue: 'Your turn' }),
			in_progress: t('T_SITE_MY_TABLES_STATUS_IN_PROGRESS', { defaultValue: 'Hand in progress' }),
			waiting: t('T_SITE_MY_TABLES_STATUS_WAITING', { defaultValue: 'Waiting for players' }),
		})[status];

	const renderGoldButton = (label: string, onPress: () => void, style?: object) => (
		<Pressable
			accessibilityRole='button'
			onPress={onPress}
			style={({ pressed }) => [styles.goldButton, style, pressed && styles.pressed]}
		>
			<GradientFill stops={MY_TABLES_GOLD_FILL} radius={BUTTON.radius * scale} />
			<Text numberOfLines={1} adjustsFontSizeToFit style={styles.goldButtonLabel}>
				{label}
			</Text>
		</Pressable>
	);

	const renderAvatar = (rawName: string) => (
		<View style={styles.avatar}>
			<Image source={tableImage(rawName)} resizeMode='cover' style={styles.avatarImage} />
		</View>
	);

	const renderActiveRow = (table: MyTable) => (
		<View key={String(table.id)} style={styles.row}>
			{renderAvatar(table.name)}
			<View style={styles.rowText}>
				<Text numberOfLines={1} style={styles.rowName}>
					{localizeTableName(table.name)}
				</Text>
				{table.status && (
					<View style={styles.inline}>
						<View style={[styles.dot, { backgroundColor: STATUS_DOT[table.status] }]} />
						<Text
							numberOfLines={1}
							style={[styles.rowMeta, table.status === 'your_turn' && styles.rowMetaTurn]}
						>
							{statusLabel(table.status)}
						</Text>
					</View>
				)}
				<View style={styles.inline}>
					<Image source={ChipStackPng} resizeMode='contain' style={styles.chipIcon} />
					<Text numberOfLines={1} style={styles.rowChips}>
						{t('T_SITE_MY_TABLES_CHIPS', {
							defaultValue: '{{amount}} Chips',
							amount: formatTableChips(table.chips),
						})}
					</Text>
				</View>
			</View>
			{renderGoldButton(t('T_SITE_MY_TABLES_RETURN', { defaultValue: 'Return' }), () =>
				handleOpen(table),
			)}
		</View>
	);

	const renderRecentRow = (table: RecentTable) => (
		<View key={table.id} style={styles.row}>
			{renderAvatar(table.name)}
			<View style={styles.rowText}>
				<Text numberOfLines={1} style={styles.rowName}>
					{localizeTableName(table.name)}
				</Text>
				<Text numberOfLines={1} style={styles.rowMeta}>
					{t('T_SITE_MY_TABLES_LAST_PLAYED', {
						defaultValue: 'Last played {{time}}',
						time: timeAgo(table.lastPlayedAt),
					})}
				</Text>
			</View>
			{table.available ? (
				<Pressable
					accessibilityRole='button'
					onPress={() => handleOpen(table)}
					style={({ pressed }) => [styles.outlineButton, pressed && styles.pressed]}
				>
					<Text numberOfLines={1} adjustsFontSizeToFit style={styles.outlineButtonLabel}>
						{t('T_SITE_MY_TABLES_REJOIN', { defaultValue: 'Rejoin' })}
					</Text>
				</Pressable>
			) : (
				<Text style={styles.closedLabel}>
					{t('T_SITE_MY_TABLES_CLOSED', { defaultValue: 'Closed' })}
				</Text>
			)}
		</View>
	);

	const renderEmpty = (title: string, text: string) => (
		<View style={styles.empty}>
			<Image source={DuelChipStackPng} resizeMode='contain' style={styles.emptyImage} />
			<Text style={styles.emptyTitle}>{title}</Text>
			<Text style={styles.emptyText}>{text}</Text>
			{renderGoldButton(
				t('T_SITE_MY_TABLES_GO_TO_LOBBY', { defaultValue: 'Go to lobby' }),
				handleGoToLobby,
				styles.lobbyButton,
			)}
		</View>
	);

	const renderContent = () => {
		const items = tab === 'active' ? active : recent;
		if (loading && items.length === 0) {
			return <ActivityIndicator color={myTablesColors.gold} style={styles.loader} />;
		}
		if (tab === 'active') {
			return active.length > 0
				? active.map(renderActiveRow)
				: renderEmpty(
						t('T_SITE_MY_TABLES_EMPTY_ACTIVE_TITLE', {
							defaultValue: "You don't have any active tables",
						}),
						t('T_SITE_MY_TABLES_EMPTY_ACTIVE_TEXT', {
							defaultValue: 'Join a table from the lobby and it will appear here.',
						}),
					);
		}
		return recent.length > 0
			? recent.map(renderRecentRow)
			: renderEmpty(
					t('T_SITE_MY_TABLES_EMPTY_RECENT_TITLE', {
						defaultValue: "You don't have any recent tables",
					}),
					t('T_SITE_MY_TABLES_EMPTY_RECENT_TEXT', {
						defaultValue: 'Tables you leave will appear here so you can rejoin them.',
					}),
				);
	};

	return (
		<RedesignModal
			scale={scale}
			onClose={onClose}
			panel={{
				height: PANEL.height,
				radius: PANEL.radius,
				overScreen: true,
				fill: PANEL_FILL,
				borderColor: myTablesColors.rim,
			}}
		>
			<View style={styles.body}>
				<View style={styles.header}>
					<View style={styles.headerText}>
						<Text style={styles.title} numberOfLines={1}>
							{t('T_SITE_MY_TABLES_TITLE', { defaultValue: 'My Tables' })}
						</Text>
						<Text style={styles.subtitle} numberOfLines={1}>
							{t('T_SITE_MY_TABLES_SUBTITLE', {
								defaultValue: 'Manage your active and recent tables',
							})}
						</Text>
					</View>
					<Pressable
						accessibilityRole='button'
						accessibilityLabel={t('T_SITE_CLOSE', { defaultValue: 'Close' })}
						onPress={onClose}
						hitSlop={8}
						style={styles.closeButton}
					>
						<Svg width={12 * scale} height={12 * scale} viewBox='0 0 12 12'>
							<Path
								d='M1 1L11 11M11 1L1 11'
								stroke='#FFFFFF'
								strokeWidth={2}
								strokeLinecap='round'
							/>
						</Svg>
					</Pressable>
				</View>

				<View style={styles.tabs} accessibilityRole='tablist'>
					{tabs.map(item => {
						const isActive = item.key === tab;
						return (
							<Pressable
								key={item.key}
								accessibilityRole='tab'
								accessibilityState={{ selected: isActive }}
								onPress={() => selectTab(item.key)}
								style={[styles.tab, isActive && styles.tabActive]}
							>
								{isActive && (
									<GradientFill stops={TAB_ACTIVE_FILL} radius={(TABS.radius - 1) * scale} />
								)}
								<Text
									numberOfLines={1}
									adjustsFontSizeToFit
									style={[styles.tabLabel, isActive && styles.tabLabelActive]}
								>
									{item.label}
								</Text>
							</Pressable>
						);
					})}
				</View>

				<RedesignScrollView
					key={tab}
					contentContainerStyle={styles.list}
					indicatorInsets={{ right: 0, top: 0, bottom: 0 }}
				>
					{renderContent()}
				</RedesignScrollView>
			</View>
		</RedesignModal>
	);
};

const createStyles = (scale: number) => {
	const s = (n: number) => n * scale;
	const hairline = Math.max(1, scale);
	const buttonLabel = {
		fontFamily: FONT,
		fontWeight: '800' as const,
		fontSize: s(12.5),
		letterSpacing: s(0.3),
		textTransform: 'uppercase' as const,
	};
	return StyleSheet.create({
		body: {
			flex: 1,
			paddingHorizontal: s(PANEL.paddingX),
			paddingVertical: s(PANEL.paddingY),
			gap: s(PANEL.gap),
		},
		header: {
			flexDirection: 'row',
			alignItems: 'flex-start',
		},
		headerText: {
			flex: 1,
			gap: s(4),
		},
		title: {
			fontFamily: FONT,
			fontWeight: '800',
			fontSize: s(24),
			lineHeight: s(28),
			color: myTablesColors.gold,
			textTransform: 'uppercase',
		},
		subtitle: {
			fontFamily: FONT,
			fontWeight: '500',
			fontSize: s(12.5),
			lineHeight: s(16),
			color: myTablesColors.secondary,
		},
		closeButton: {
			width: s(CLOSE),
			height: s(CLOSE),
			borderRadius: s(CLOSE / 2),
			alignItems: 'center',
			justifyContent: 'center',
			backgroundColor: 'rgba(255,255,255,0.12)',
		},
		tabs: {
			flexDirection: 'row',
			height: s(TABS.height),
			borderRadius: s(TABS.radius),
			borderWidth: hairline,
			borderColor: myTablesColors.rowBorder,
			backgroundColor: 'rgba(0,0,0,0.25)',
		},
		tab: {
			flex: 1,
			alignItems: 'center',
			justifyContent: 'center',
			paddingHorizontal: s(6),
			borderRadius: s(TABS.radius),
			overflow: 'hidden',
		},
		tabActive: {
			borderWidth: Math.max(1, s(1.5)),
			borderColor: '#E8A33A',
			margin: -hairline,
			shadowColor: '#FF9C00',
			shadowOffset: { width: 0, height: 0 },
			shadowOpacity: 0.35,
			shadowRadius: s(6),
		},
		tabLabel: {
			fontFamily: FONT,
			fontWeight: '600',
			fontSize: s(11.5),
			letterSpacing: s(0.3),
			textTransform: 'uppercase',
			textAlign: 'center',
			color: myTablesColors.secondary,
		},
		tabLabelActive: {
			fontWeight: '800',
			color: myTablesColors.gold,
		},
		list: {
			gap: s(8),
			paddingBottom: s(4),
		},
		row: {
			flexDirection: 'row',
			alignItems: 'center',
			gap: s(12),
			paddingVertical: s(10),
			paddingLeft: s(8),
			paddingRight: s(10),
			borderRadius: s(ROW.radius),
			borderWidth: hairline,
			borderColor: myTablesColors.rowBorder,
			backgroundColor: myTablesColors.row,
		},
		avatar: {
			width: s(ROW.avatar),
			height: s(ROW.avatar),
			borderRadius: s(ROW.avatar / 2),
			borderWidth: Math.max(1, s(2)),
			borderColor: myTablesColors.rim,
			backgroundColor: myTablesColors.well,
			overflow: 'hidden',
		},
		// The city art is a portrait plinth with the landmark in its upper half; scaled past the
		// circle and nudged down so the circle frames the landmark rather than the base.
		avatarImage: {
			position: 'absolute',
			width: s(ROW.avatar * 1.3),
			height: s(ROW.avatar * 1.3 * (4 / 3)),
			left: s(-ROW.avatar * 0.15),
			top: s(-ROW.avatar * 0.2),
		},
		rowText: {
			flex: 1,
			gap: s(4),
		},
		rowName: {
			fontFamily: FONT,
			fontWeight: '600',
			fontSize: s(15),
			lineHeight: s(18),
			color: '#FFFFFF',
		},
		inline: {
			flexDirection: 'row',
			alignItems: 'center',
			gap: s(6),
		},
		dot: {
			width: s(8),
			height: s(8),
			borderRadius: s(4),
		},
		chipIcon: {
			width: s(20),
			height: s(12),
		},
		rowMeta: {
			flexShrink: 1,
			fontFamily: FONT,
			fontWeight: '500',
			fontSize: s(11.5),
			lineHeight: s(14),
			color: myTablesColors.secondary,
		},
		rowMetaTurn: {
			color: myTablesColors.green,
		},
		rowChips: {
			flexShrink: 1,
			fontFamily: FONT,
			fontWeight: '700',
			fontSize: s(11.5),
			lineHeight: s(14),
			color: '#FFFFFF',
		},
		goldButton: {
			width: s(BUTTON.width),
			height: s(BUTTON.height),
			alignItems: 'center',
			justifyContent: 'center',
			paddingHorizontal: s(6),
			borderRadius: s(BUTTON.radius),
			shadowColor: '#FFB800',
			shadowOffset: { width: 0, height: 0 },
			shadowOpacity: 0.3,
			shadowRadius: s(6),
		},
		goldButtonLabel: {
			...buttonLabel,
			color: myTablesColors.onGold,
		},
		outlineButton: {
			width: s(BUTTON.width),
			height: s(BUTTON.height - 2),
			alignItems: 'center',
			justifyContent: 'center',
			paddingHorizontal: s(6),
			borderRadius: s(BUTTON.radius),
			borderWidth: Math.max(1, s(1.5)),
			borderColor: myTablesColors.gold,
			backgroundColor: 'rgba(0,0,0,0.2)',
		},
		outlineButtonLabel: {
			...buttonLabel,
			color: myTablesColors.gold,
		},
		closedLabel: {
			width: s(BUTTON.width),
			textAlign: 'center',
			fontFamily: FONT,
			fontWeight: '700',
			fontSize: s(11.5),
			textTransform: 'uppercase',
			color: myTablesColors.muted,
		},
		pressed: {
			opacity: 0.85,
		},
		empty: {
			alignItems: 'center',
			paddingTop: s(44),
			gap: s(6),
		},
		emptyImage: {
			width: s(96),
			height: s(96),
			marginBottom: s(8),
		},
		emptyTitle: {
			fontFamily: FONT,
			fontWeight: '500',
			fontSize: s(17),
			lineHeight: s(22),
			textAlign: 'center',
			color: '#FFFFFF',
		},
		emptyText: {
			fontFamily: FONT,
			fontWeight: '400',
			fontSize: s(12.5),
			lineHeight: s(17),
			textAlign: 'center',
			color: myTablesColors.secondary,
		},
		lobbyButton: {
			width: s(152),
			height: s(40),
			marginTop: s(24),
		},
		loader: {
			marginTop: s(80),
		},
	});
};

export default MyTablesModal;
